import { warn, debug } from "rtUtils/logger";
import { getCloudFromConfig } from "../../utils";

const EVENTS = {
	RESOURCE_LIST_LOAD: "tag-resource-list-json-load",
	RESOURCE_NOT_FOUND: "tag-resource-not-found",
}

const getListUrl = (baseUrl, cloudName, rType) => `${baseUrl}${cloudName}/${rType}/list/`;

//save cache as long as page is alive
let previousLists = {};

const reportFetchError = (url, response, triggerWithAnalytics) => {
	warn("Cloudinary 3D - VIEWER - failed to retrieve asset resource list JSON", { url, response });

	triggerWithAnalytics(EVENTS.RESOURCE_LIST_LOAD, {...response, error: true });

	return {
		response,
	};
};

const getTagResourceJson = (url, triggerWithAnalytics) => {
	return fetch(url)
		.then((response) => {
			const { status, statusText } = response;

			debug("Cloudinary 3D - VIEWER - retrieved response for resource list JSON", {
				status,
				statusText,
			});

			return status === 200 ?
				response.json()
					.then((jsonRes) => ({ jsonRes, response })) :
				reportFetchError(url, response, triggerWithAnalytics);
		})
		.then(({ jsonRes, response }) => {
			return {
				success: !!jsonRes,
				data: jsonRes,
			};
		});
};

const getResourcesFromList = async (config, id, preset, resourceType, triggerWithAnalytics, tagPrefix = "3d-path-", tagSuffix = "") => {
	const cloudName = getCloudFromConfig(config);
	const tag = `${tagPrefix}${id}$${preset}${tagSuffix}`;
	const cacheKey = `${resourceType}/${tag}`;

	let cldList = previousLists[cacheKey];

	if (!cldList) {
		const tagUrl = getListUrl(config.baseUrl, cloudName, resourceType) + tag + ".json";
		debug(`Cloudinary 3D - ${resourceType.toUpperCase()} VIEWER - fetching resource list JSON`, { tagUrl });
		const listJson = await getTagResourceJson(tagUrl, triggerWithAnalytics);

		if (listJson.success) {
			cldList = listJson.data.resources;
			previousLists[cacheKey] = cldList;
		}
	}

	return cldList;
};

/**
 *
 * @param config the configuration object
 * @param id the product sku
 * @param preset the preset publish name
 * @param template the template publish name
 * @param resourceType image or video
 * @param triggerWithAnalytics
 * @returns {*|Promise<{success, data: *}>}
 */
const getArtifactPublicIdFromResourceList = async (config, id, preset, template, resourceType, triggerWithAnalytics) => {
	const cldList = await getResourcesFromList(config, id, preset, resourceType, triggerWithAnalytics);

	if (cldList) {
		const resource = cldList.find((r) => r.public_id.endsWith(template));
		if (resource) {
			return resource.public_id;
		}
		triggerWithAnalytics(EVENTS.RESOURCE_NOT_FOUND, {
			error: true,
			msg: `${template} not found as public id in retrieved resources`
		});
	}
	return null;
};

const getSpinsetPublicIdsFromResourceList = async (config, id, preset, template, triggerWithAnalytics) => {
	const cldList = await getResourcesFromList(config, id, preset, "image", triggerWithAnalytics, "3d-", "-spinset");

	if (cldList) {
		const resources = cldList
			.filter((r) => new RegExp(`${template}_\\d+$`).test(r.public_id))
			.sort((a, b) => {
				const aIndex = parseInt(a.public_id.split('_').pop(), 10);
				const bIndex = parseInt(b.public_id.split('_').pop(), 10);
				return aIndex - bIndex;
			});

		if (resources.length > 0) {
			return resources.map(r => r.public_id);
		}
		triggerWithAnalytics(EVENTS.RESOURCE_NOT_FOUND, {
			error: true,
			msg: `No spinset frames found for template ${template}`
		});
	}
	return null;
};

//currently used for tests only
const clearPreviousLists = () => {
	previousLists = {};
};

export {
	getArtifactPublicIdFromResourceList,
	getSpinsetPublicIdsFromResourceList,
	clearPreviousLists,
}
